import React, { useState, useEffect, useRef } from 'react';
import SlidingPane from 'react-sliding-pane';
import 'react-sliding-pane/dist/react-sliding-pane.css';
import { Button, Checkbox, List, Select, Progress, Flex } from 'antd';
import { useForm, Controller } from "react-hook-form";
import { LeftOutlined, RightOutlined, CheckCircleFilled, CloseCircleFilled, PlayCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import Axios from '../../config/axios';
import { toast, confirm } from '@rickylandino/react-messages';

export default function ExhibitorBadgesSlider(props) {
    const [state, setState] = useState({
        printBadgesMode: '',
        exhibitorsLoaded: false,
        selectedTargetShow: props.showToPrintBadgesFor.showcode,
        selectedShowAbbrev: props.showToPrintBadgesFor.show_abbrev
    });

    const userInfo = JSON.parse(window.sessionStorage.getItem("userInfo"));

    const { register, getValues, setValue, control, watch } = useForm();
    const [badgePrinterStatus, setBadgePrinterStatus] = useState(['']);
    const [exhibitorBadges, setExhibitorBadges] = useState([]);
    const [checkedItems, setCheckedItems] = useState([]);
    const [originalCheckedItems, setOriginalCheckedItems] = useState([]);
    const [selectedBadge, setSelectedBadge] = useState(null);
    const [deletedBadgeList, setDeletedBadgeList] = useState([]);
    const [originalExhibitorBadges, setOriginalExhibitorBadges] = useState([]);
    const [exhibitorBadgesIndex, setExhibitorBadgesIndex] = useState(0);
    const [exhibitors, setExhibitors] = useState([]);

    const batchPausedRef = useRef(false);         // used to pause the batch
    const [batchProgress, setBatchProgress] = useState(0);  // progress bar %

    const toggleItem = (id) => {
        setCheckedItems((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    // Calculate max label length for alignment
    const maxLabelLength = Math.max(
        ...exhibitorBadges.map((b) => `${b.badge.fName} ${b.badge.lName}`.length)
    );
    const labelColumnWidth = maxLabelLength * 8;

    useEffect(() => {
        setExhibitors(props.exhibitors);
        setExhibitorBadgesIndex(0);

        setValue("formFields.batchSize", '10');

        setState({
            ...state,
            selectedTargetShow: props.showToPrintBadgesFor.showcode,
            exhibitorsLoaded: true
        });

    }, [props]);

    useEffect(() => {
        setExhibitors(props.exhibitors);
        setExhibitorBadgesIndex(props.selectedIndex);

        if (state.exhibitorsLoaded) {
            LoadSelectedExhibitorBadges(props.selectedIndex);

            setState({
                ...state,
                printBadgesMode: props.printBadgesMode,
                showPane: true
            });
        }
    }, [state.exhibitorsLoaded]);

    useEffect(() => {
        if (exhibitorBadges?.length) {
            const initialChecked = Array.from(
                new Set(
                    exhibitorBadges
                        .filter((b) => b.badge.shows?.includes(state.selectedTargetShow.toString()))
                        .map((b) => b.badge.exhibitorBadges_ID)
                )
            );

            setCheckedItems(initialChecked);
            setOriginalCheckedItems(initialChecked);
        }       
    }, [exhibitorBadges]);

    function handleEditBadge() {
        if (selectedBadge !== null) {
            loadBadgeDetails();

            setState({
                ...state,
                badgeEditMode: 'Edit'
            });
        }
        else {
            toast.error('You must select a badge to edit');
        }
    }

    function loadBadgeDetails() {
        handleClearFields();

        setValue('formFields.firstName', selectedBadge.fName);
        setValue('formFields.lastName', selectedBadge.lName);
        setValue('formFields.title', selectedBadge.title);
        setValue('formFields.address', selectedBadge.address);
        setValue('formFields.city', selectedBadge.city);
        setValue('formFields.state', selectedBadge.state);
        setValue('formFields.zip', selectedBadge.zip);
        setValue('formFields.phone', selectedBadge.phone);
        setValue('formFields.fax', selectedBadge.fax);
        setValue('formFields.email', selectedBadge.email);

        setState({
            ...state
        });
    }

    function handleClearFields() {
        setValue('formFields.firstName', '');
        setValue('formFields.lastName', '');
        setValue('formFields.title', '');
        setValue('formFields.address', '');
        setValue('formFields.city', '');
        setValue('formFields.state', '');
        setValue('formFields.zip', '');
        setValue('formFields.phone', '');
        setValue('formFields.fax', '');
        setValue('formFields.email', '');

        setState({
            ...state,
            badgeEditMode: 'Add'
        });
    }

    function handleDeleteBadge() {
        if (selectedBadge !== null) {
            confirm({
                title: "You are about to permanently delete this badge from all shows for this exhibitor",
                content: "Are you sure you would like continue?",
                buttons: ["Yes", "No"],
            }, (buttonPressed) => {
                if (buttonPressed === 'Yes') {
                    //only add to deletedBadgeList if it's not a new badge added in this session
                    exhibitorBadges.forEach(badgeObj => {
                        if (badgeObj.badge.exhibitorBadges_ID === selectedBadge.exhibitorBadges_ID && badgeObj.type !== 'new') {
                            // 2?? Add the deleted badge ID to deletedBadgeList
                            setDeletedBadgeList(prevList => [...prevList, badgeObj]);
                        }
                    });

                    // 1?? Remove the badge from exhibitorBadges
                    setExhibitorBadges(prevBadges =>
                        prevBadges.filter(b => b.badge.exhibitorBadges_ID !== selectedBadge.exhibitorBadges_ID)
                    );

                    return 0;
                } else {
                    return 0;
                }
            });
        }
        else {
            toast.error('You must select a badge to delete');
        }
    }

    function handlePrintSelectedBadge() {
        if (selectedBadge !== null) {
            printExhibitorBadgesForShow('Single', exhibitorBadges);
        }
        else {
            toast.error('You must select a badge to print');
        }
    }

    const delay = (ms) => new Promise(res => setTimeout(res, ms));

    async function printExhibitorBadgesForShow(printMode, badgesSource) {
        if (getValues().formFields.JSS_BadgeCompany1.trim() === '') {
            toast.error('Company Name Must Be Provided');
            return;
        }

        let badgesToPrint = [];

        if (printMode === 'All') {
            // Only print checked badges
            badgesToPrint = badgesSource.filter(b => b.badge.shows?.includes(state.selectedTargetShow.toString())).map(b => b.badge);

        } else if (printMode === 'Single') {
            if (selectedBadge) {
                badgesToPrint.push(selectedBadge);
            } else {
                toast.error('You must select a badge to print');
                return;
            }
        }

        if (badgesToPrint.length === 0) {
            toast.error('No badges selected to print');
            return;
        }

        const postdata = {
            badgeList: badgesToPrint,
            showcode: state.selectedTargetShow.toString(),
            show_abbrev: state.selectedShowAbbrev || '',
            company1: getValues().formFields.JSS_BadgeCompany1,
            company2: getValues().formFields.JSS_BadgeCompany2
        };

        try {
            await Axios.post('/api/PrintExhibitorBadges', postdata);
            toast.success(
                badgesToPrint.length === 1
                    ? '1 Exhibitor Badge Printed Successfully'
                    : `${badgesToPrint.length} Exhibitor Badges Printed Successfully`
            );
        } catch (error) {
            console.error(error);
            toast.error('Error printing badges');
        }
    }

    function handlePrintAllShowBadges() {
        const badgesToPrint = exhibitorBadges.filter(b => b.badge.shows?.includes(state.selectedTargetShow.toString())).map(b => b.badge);

        if (badgesToPrint.length === 0) {
            toast.error('No badges selected to print for this exhibitor');
            return;
        }

        printExhibitorBadgesForShow('All', exhibitorBadges);
    }

    function handleUpdateBadge() {
        if (state.badgeEditMode === 'Add') {
            // 1?? Find the max existing rowID
            const maxRowID = exhibitorBadges.reduce((max, b) => {
                const id = parseInt(b.rowID, 10); // convert string to number
                return id > max ? id : max;
            }, 0);

            const newRowID = (maxRowID + 1);

            const newBadge = {
                exhibitorBadges_ID: newRowID,
                fName: getValues().formFields.firstName,
                lName: getValues().formFields.lastName,
                title: getValues().formFields.title,
                address: getValues().formFields.address,
                city: getValues().formFields.city,
                state: getValues().formFields.state,
                zip: getValues().formFields.zip,
                phone: getValues().formFields.phone,
                fax: getValues().formFields.fax,
                email: getValues().formFields.email,
                shows: "|" + state.selectedTargetShow + "|"
            };

            const newBVM = {
                type: 'new',
                rowID: newRowID.toString(),
                isSelected: true,
                badge: newBadge
            };

            //Note: checkedItems gets updated in the corresponding useEffect when exhibitorBadges gets changed below

            setExhibitorBadges(prev => [...prev, newBVM]);

            handleClearFields();
        }
        else {
            const editedBadge = {
                ...selectedBadge, // keep the original ID and other fields
                fName: getValues().formFields.firstName,
                lastName: getValues().formFields.lastName,
                title: getValues().formFields.title,
                address: getValues().formFields.address,
                city: getValues().formFields.city,
                state: getValues().formFields.state,
                zip: getValues().formFields.zip,
                phone: getValues().formFields.phone,
                fax: getValues().formFields.fax,
                email: getValues().formFields.email
            };

            const updatedBadges = exhibitorBadges.map(b => {
                if (b.badge.exhibitorBadges_ID === selectedBadge.exhibitorBadges_ID) {
                    return { ...b, isDirty: true, badge: editedBadge };
                }
                return b;
            });

            setExhibitorBadges(updatedBadges);

            handleClearFields();

            setSelectedBadge(editedBadge);
        }
    }

    const hidePrintBadgesPane = () => {
        //const doChecksMatch = JSON.stringify(checkedItems) === JSON.stringify(originalCheckedItems);
        //console.log(doChecksMatch);

        //const doBadgesMatch = JSON.stringify(exhibitorBadges) === JSON.stringify(originalExhibitorBadges);
        //console.log(doBadgesMatch);

        const badgeDataChangedChanged = JSON.stringify(exhibitorBadges) !== JSON.stringify(originalExhibitorBadges) || JSON.stringify(checkedItems) !== JSON.stringify(originalCheckedItems);

        if (badgeDataChangedChanged) {
            confirm({
                title: "Changes you have made will not be saved",
                content: "Are you sure you would like continue?",
                buttons: ["Yes", "No"],
            }, (buttonPressed) => {
                if (buttonPressed === 'Yes') {
                    setState({
                        ...state,
                        showPane: false
                    });

                    if (props.badgesSliderCallingComponent === 'SuppliedServicesMgr') {
                        props.updateExhibitorsAfterSave(exhibitors);
                    }

                    setTimeout(() => {
                        props.hidePane();
                    }, 1000);

                    return 0;
                } else {
                    return 0;
                }
            });
        }
        else {
            setState({
                ...state,
                showPane: false
            });

            if (props.badgesSliderCallingComponent === 'SuppliedServicesMgr') {
                props.updateExhibitorsAfterSave(exhibitors);
            }

            setTimeout(() => {
                props.hidePane();
            }, 1000);
        }
    };

    function handleSaveChanges() {
        //this sets isChecked for each object
        const badgeList = exhibitorBadges.map(b => ({
            ...b,
            isSelected: checkedItems.includes(b.badge.exhibitorBadges_ID)
        }));

        ////this updates the list of badges for the selected exhibitor before sending it back to the calling component
        //const updatedExhibitors = exhibitors.map((ex, idx) =>
        //    idx === exhibitorBadgesIndex
        //        ? { ...ex, exhibitorBadges: badgeList }
        //        : ex
        //);

        //setExhibitors(updatedExhibitors);

        let myExhibitorId = exhibitors[exhibitorBadgesIndex].id;

        let postdata = {
            id: myExhibitorId,
            showcode: state.selectedTargetShow.toString(),
            pastDeadline: false,            //not used for anything here
            badgeList: badgeList,
            deletedBadgeList: deletedBadgeList
        }

        Axios.post(`/api/UpdateExhibitorBadges`, postdata
        ).then(response => {
            if (response.data) {
                toast.success('Exhibitor Badges Successfully Updated!');

                //console.log(updatedExhibitors);

                setState({
                    ...state,
                    showPane: false
                });

                setTimeout(() => {
                    if (props.badgesSliderCallingComponent === 'SuppliedServicesMgr') {
                        const newFilteredExhibitors = exhibitors.map(ex =>
                            ex.id === myExhibitorId
                                ? {
                                    ...ex,
                                    exhibitorBadges: response.data.masterBadgeList   // your updated array
                                }
                                : ex
                        );

                        props.updateExhibitorsAfterSave(newFilteredExhibitors);
                    }

                    props.hidePane();
                }, 1000);
            }
        }).catch(error => {
            console.log(error);
        });
    }

    function handleGoToExhibitor() {
        setSelectedBadge(null);
        handleClearFields();

        var myGoToIndex = parseInt(getValues().formFields.goToExhibitorIndex, 10) - 1;

        setExhibitorBadgesIndex(myGoToIndex);

        setValue('formFields.goToExhibitorIndex', '');

        LoadSelectedExhibitorBadges(myGoToIndex);

        setState({
            ...state
        });
    }

    function handlePrev() {
        //const badgeDataChangedChanged = JSON.stringify(exhibitorBadges) !== JSON.stringify(originalExhibitorBadges);
        const badgeDataChangedChanged = JSON.stringify(exhibitorBadges) !== JSON.stringify(originalExhibitorBadges) || JSON.stringify(checkedItems) !== JSON.stringify(originalCheckedItems);

        if (badgeDataChangedChanged) {
            confirm({
                title: "Changes you have made will not be saved",
                content: "Are you sure you would like continue?",
                buttons: ["Yes", "No"],
            }, (buttonPressed) => {
                if (buttonPressed === 'Yes') {
                    setSelectedBadge(null);
                    handleClearFields();

                    let myPrevIndex = exhibitorBadgesIndex - 1;
                    LoadSelectedExhibitorBadges(myPrevIndex);

                    return 0;
                } else {
                    return 0;
                }
            });
        }
        else {
            setSelectedBadge(null);
            handleClearFields();

            let myPrevIndex = exhibitorBadgesIndex - 1;
            LoadSelectedExhibitorBadges(myPrevIndex);

        }
    }

    function handleNext() {
        //const badgeDataChangedChanged = JSON.stringify(exhibitorBadges) !== JSON.stringify(originalExhibitorBadges);
        const badgeDataChangedChanged = JSON.stringify(exhibitorBadges) !== JSON.stringify(originalExhibitorBadges) || JSON.stringify(checkedItems) !== JSON.stringify(originalCheckedItems);

        if (badgeDataChangedChanged) {
            confirm({
                title: "Changes you have made will not be saved",
                content: "Are you sure you would like continue?",
                buttons: ["Yes", "No"],
            }, (buttonPressed) => {
                if (buttonPressed === 'Yes') {
                    setSelectedBadge(null);
                    handleClearFields();

                    let myNextIndex = exhibitorBadgesIndex + 1;
                    LoadSelectedExhibitorBadges(myNextIndex);

                    return 0;
                } else {
                    return 0;
                }
            });
        }
        else {
            setSelectedBadge(null);
            handleClearFields();

            let myNextIndex = exhibitorBadgesIndex + 1;
            LoadSelectedExhibitorBadges(myNextIndex);
        }
    }

    async function LoadSelectedExhibitorBadges(index) {
        const exhibitor = exhibitors[index];
        const badges = exhibitor.exhibitorBadges || [];

        console.log(exhibitor);

        // Update form fields
        setValue("formFields.JSS_BadgeCompany1", exhibitor.jsS_BadgeCompany1);
        setValue("formFields.JSS_BadgeCompany2", exhibitor.jsS_BadgeCompany2);

        // Update state for badges
        setExhibitorBadges(badges);
        setOriginalExhibitorBadges(badges);
        setDeletedBadgeList([]);
        setExhibitorBadgesIndex(index);

        // Check printer status
        try {
            const response = await Axios.get('/api/IsBadgePrinterOnline');
            setBadgePrinterStatus(response.data === true ? 'Online' : 'Offline');

            //setBadgePrinterStatus('Online');
        } catch (error) {
            console.log(error);
        }

        return badges;
    }

    const handleStartBatch = async () => {
        const batchSize = parseInt(getValues().formFields.batchSize, 10) || 1;
        batchPausedRef.current = false;

        let currentIndex = exhibitorBadgesIndex;

        for (let i = 0; i < batchSize; i++) {
            if (batchPausedRef.current) break;

            // Load badges for current exhibitor and sync checkedItems
            const badges = await LoadSelectedExhibitorBadges(currentIndex);

            // Print only the checked badges
            await printExhibitorBadgesForShow('All', badges);

            await delay(1000);

            if (currentIndex < exhibitors.length - 1) {
                currentIndex++;
            } else {
                break;
            }

            setBatchProgress(Math.round(((currentIndex + 1) / exhibitors.length) * 100));
        }

        //bumps it to the next exhibitor if it's not at the end
        if (currentIndex < exhibitors.length) {
            setExhibitorBadgesIndex(currentIndex);
        }
    };

    const handlePauseBatch = () => {
        batchPausedRef.current = true;
    };


    return (
        <>
            <SlidingPane
                className='some-custom-class'
                overlayClassName='showCard'
                isOpen={state.showPane}
                title='Manage Exhibitor Badges'
                onRequestClose={hidePrintBadgesPane}
                closeIcon={<i className="fas fa-chevron-right float-left"></i>}
                width="50%"
            >
                {exhibitors?.length > 0 && (
                    <div className="slide-pane-body scrollableDiv">
                        {state.printBadgesMode === 'All' &&
                            <>
                                <div className="form-group col-12 padding-25-10 mb-3"
                                    style={{ display: "flex", alignItems: "center" }}>
                                    {/*Play, pause icons*/}
                                    <label className="form-label">Batch Print Size&nbsp;</label>
                                    <Select
                                        value={getValues().formFields.batchSize}
                                    onChange={(value) => { setValue("formFields.batchSize", value); setState({ ...state }); }}
                                        style={{ width: 60 }}
                                        options={[
                                            { value: 1, label: '1' },
                                            { value: 5, label: '5' },
                                            { value: 10, label: '10' }
                                        ]}
                                    />

                                    <Button type="btn btn-outline-primary ms-1" icon={<PlayCircleOutlined />} onClick={handleStartBatch} disabled={badgePrinterStatus === 'Offline'}></Button>
                                    <Button type="btn btn-outline-primary ms-1 me-2" icon={<PauseCircleOutlined />} onClick={handlePauseBatch} disabled={badgePrinterStatus === 'Offline'}></Button>

                                    {/*<Button className="btn-outline-primary ms-1" icon={<PlayCircleOutlined />} onClick={handleStartBatch} disabled={badgePrinterStatus === 'Offline' ></Button>*/}
                                    {/*<Button className="btn-outline-primary ms-1 me-2" icon={<PauseCircleOutlined />} onClick={handlePauseBatch} disabled={badgePrinterStatus === 'Offline'></Button>*/}

                                    <div style={{ width: 180, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <Progress
                                            percent={
                                                exhibitors.length > 0
                                                    ? Math.round(((exhibitorBadgesIndex + 1) / exhibitors.length) * 100)
                                                    : 0
                                            }
                                            size="small"
                                        />
                                        <span style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                                            Exhibitor {exhibitorBadgesIndex + 1} of {exhibitors.length}
                                        </span>
                                    </div>

                                    {/* RIGHT JUSTIFIED GROUP */}
                                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
                                        {/*Prev, Next icons*/}
                                        <Button type="primary" icon={<LeftOutlined />} onClick={handlePrev} disabled={exhibitorBadgesIndex === 0} className="me-1 btn btn-sm">{/*Previous*/}</Button>
                                        <Button type="primary" icon={<RightOutlined />} onClick={handleNext} disabled={exhibitorBadgesIndex === exhibitors.length - 1} className="btn btn-sm">{/*Next*/}</Button>

                                        {'Exhibitor ' + (exhibitorBadgesIndex + 1) + ' of ' + exhibitors.length}

                                        <label className="form-label">Go To Exhibitor</label>&nbsp;
                                        <input type="text" name="goToExhibitorIndex" {...register("formFields.goToExhibitorIndex")} className="inline-text-box" style={{ width: "50px" }} onInput={(e) => e.target.value = e.target.value.replace(/\D/g, '')} />
                                        <Button type="link" onClick={handleGoToExhibitor} ><u>Go</u></Button>
                                    </div>
                                </div>
                            </>
                        }

                        <div className="row align-items-center mb-2">
                            <div className="col-6">
                                <span className="form-data-label fw-bold">
                                    {exhibitors?.[exhibitorBadgesIndex]?.name || ''}
                                </span>
                            </div>



                        </div>
                        <div className="col col-12 mb-3">
                            <strong>Note: </strong>
                            Any name or title changes (additions, deletions, changes) made to badges here will
                            also change the master badge list for this exhibitor. To include a specific badge
                            for this show, simply check the box next to the name to be included.
                        </div>



                        <div className="row pb-3">
                            <div className="col col-4">
                                <strong>Company</strong><br />
                                <input type="text" name="JSS_BadgeCompany1" {...register("formFields.JSS_BadgeCompany1")} className="form-control-custom" />
                                <input type="text" name="JSS_BadgeCompany2" {...register("formFields.JSS_BadgeCompany2")} className="form-control-custom pt-1" />

                                <div className="pt-4">
                                    Badge Printer Status:&nbsp;
                                    {badgePrinterStatus === 'Online' ? (
                                        <span style={{ fontWeight: 'bold', color: 'green' }}>
                                            <CheckCircleFilled style={{ marginRight: '5px', position: 'relative', top: '-2px' }} />
                                            Online
                                        </span>
                                    ) : (
                                        <span style={{ fontWeight: 'bold', color: 'red' }}>
                                            <CloseCircleFilled style={{ marginRight: '5px', position: 'relative', top: '-2px' }} />
                                            Offline
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="col col-8">
                                <div className="col col-12">
                                    <strong>Personnel Badges</strong><br />
                                    <div
                                        style={{
                                            border: "1px solid #d9d9d9",
                                            borderRadius: 6,
                                            maxWidth: 500,
                                            background: "#fff",
                                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                            padding: "10px 14px",
                                        }}
                                    >
                                        <div
                                            style={{
                                                height: "200px", // <-- adjust this as needed
                                                overflowY: "auto",
                                                overflowX: "hidden",
                                            }}
                                        >
                                            <List
                                                size="small"
                                                dataSource={exhibitorBadges}
                                                renderItem={(item) => {
                                                    const isChecked = checkedItems.includes(item.badge.exhibitorBadges_ID);
                                                    const isSelected = selectedBadge === item.badge;

                                                    return (
                                                        <List.Item
                                                            style={{
                                                                padding: "8px 6px",
                                                                cursor: "pointer",
                                                                background: isSelected
                                                                    ? "#e6f7ff"
                                                                    : isChecked
                                                                        ? "#fff"
                                                                        : "#fff",
                                                                transition: "background 0.18s",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                borderRadius: 4,
                                                            }}
                                                            onClick={() => setSelectedBadge(item.badge)}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Checkbox
                                                                    checked={isChecked}
                                                                    onChange={() => toggleItem(item.badge.exhibitorBadges_ID)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    style={{
                                                                        marginRight: 12,
                                                                        flexShrink: 0,
                                                                        border: "1px solid #d9d9d9",
                                                                        borderRadius: 2,
                                                                        backgroundColor: "#fff",
                                                                        padding: "2px",
                                                                    }}
                                                                />
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        width: "100%",
                                                                        overflow: "hidden",
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            width: labelColumnWidth,
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {item.badge.fName} {item.badge.lName}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            flex: 1,
                                                                            textAlign: "left",
                                                                            color: "#333",
                                                                        }}
                                                                    >
                                                                        {item.badge.title}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </List.Item>
                                                    );
                                                }}
                                            />
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>

                        <div className="row pt-3">
                            {/* buttons row */}
                            <div className="col-lg-2">
                                <Button type="btn btn-outline-primary" onClick={handleEditBadge} >Edit Badge</Button>
                            </div>
                            <div className="col col-2">
                                <Button type="btn btn-outline-primary" onClick={handleDeleteBadge} >Delete Badge</Button>
                            </div>
                            <div className="col col-1">

                            </div>

                            <div className="col col-3">
                                <Button type="btn btn-outline-primary" onClick={handlePrintSelectedBadge} disabled={badgePrinterStatus === 'Offline'}>Print Selected Badge</Button>
                                {/*<Button type="btn btn-outline-primary" onClick={handlePrintSelectedBadge}>Print Selected Badge</Button>*/}
                            </div>
                            <div className="col col-3">
                                <Button type="btn btn-outline-primary" onClick={handlePrintAllShowBadges} disabled={badgePrinterStatus === 'Offline'}>Print All Badges For Show</Button>
                                {/*<Button type="btn btn-outline-primary" onClick={handlePrintAllShowBadges}>Print All Badges For Show</Button>*/}
                            </div>
                        </div>

                        <div className="form-group col-lg-12 pt-4 pb-2">
                            <strong>New Badge</strong>
                        </div>

                        <div className="row pt-3" style={{
                            border: "1px solid #d9d9d9",
                            borderRadius: 6,
                            background: "#fff",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                            padding: "10px 14px",
                        }}>
                            {/*row 1*/}
                            <div className="form-group col-lg-3">
                                <label className="form-label">First Name</label>
                                <input type="text" placeholder="First Name" {...register("formFields.firstName")} className="form-control-custom" />
                            </div>
                            <div className="col col-4">
                                <label className="form-label">Last Name</label>
                                <input type="text" placeholder="Last Name" {...register("formFields.lastName")} className="form-control-custom" />
                            </div>
                            <div className="col col-4">
                                <label className="form-label">Title</label>
                                <input type="text" placeholder="Title" {...register("formFields.title")} className="form-control-custom" />
                            </div>
                            <div className="col col-1"></div>

                            {/*row 2*/}
                            <div className="form-group col-lg-4">
                                <label className="form-label">Address</label>
                                <input type="text" placeholder="Address" {...register("formFields.address")} className="form-control-custom" />
                            </div>
                            <div className="form-group col-lg-4">
                                <label className="form-label">City</label>
                                <input type="text" placeholder="City" {...register("formFields.city")} className="form-control-custom" />
                            </div>
                            <div className="form-group col-lg-2">
                                <label className="form-label">State</label>
                                <input type="text" placeholder="State" {...register("formFields.state")} className="form-control-custom" />
                            </div>
                            <div className="form-group col-lg-2">
                                <label className="form-label">Zip</label>
                                <input type="text" placeholder="Zip" {...register("formFields.zip")} className="form-control-custom" />
                            </div>

                            {/*row 2*/}
                            <div className="form-group col-lg-3">
                                <label className="form-label">Phone</label>
                                <input type="text" placeholder="Phone" {...register("formFields.phone")} className="form-control-custom" />
                            </div>
                            <div className="form-group col-lg-3">
                                <label className="form-label">Fax</label>
                                <input type="text" placeholder="Fax" {...register("formFields.fax")} className="form-control-custom" />
                            </div>
                            <div className="form-group col-lg-6">
                                <label className="form-label">Email</label>
                                <input type="text" placeholder="Email" {...register("formFields.email")} className="form-control-custom" />
                            </div>

                            {/*buttons 2*/}
                            {/*<div className="form-group col-lg-4">*/}
                            {/*    {state.badgeEditMode === 'Edit' &&*/}
                            {/*        <Button type="link" onClick={handleGetInfoFromContactRecord} ><u>Get Info From Contact Record</u></Button>*/}
                            {/*    }*/}
                            {/*</div>*/}
                            <div className="form-group col-lg-3">
                                <Button type="btn btn-outline-primary" onClick={handleClearFields} >Clear Fields</Button>
                            </div>
                            <div className="form-group col-lg-4">
                                <Button type="btn btn-outline-primary" onClick={handleUpdateBadge} >{state.badgeEditMode === 'Add' ? 'Add Badge' : 'Update Badge'}</Button>
                            </div>
                            <div className="form-group col-lg-5"></div>
                        </div>

                        <div className="row pt-4">
                            {/*<div className="col col-12">*/}
                            {/*    <strong>Personnel Badges</strong>&nbsp;&nbsp;&nbsp;*/}
                            {/*</div>*/}

                        </div>
                    </div>
                )}
                <div className="modal-footer">
                    <div className="form-group col-12 padding-25-10"
                        style={{ display: "flex", alignItems: "center" }}>

                        <button className="btn btn-submit margin-left-15 me-3" onClick={hidePrintBadgesPane}>
                            Close
                        </button>

                        <button className="btn btn-submit margin-left-15" onClick={handleSaveChanges}>
                            Save Changes
                        </button>

                        {/* RIGHT JUSTIFIED GROUP */}
                        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
                            
                        </div>
                    </div>
                </div>
            </SlidingPane>
        </>
    );
}